import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, filter, Observable, of } from 'rxjs';
import { Router } from '@angular/router';
import { environment } from '../../../../my-app/src/environments/environment';
import { GlobalService } from './global.service';
import { users } from '../../../../my-app/public/assets/role-map/role-map';
import { RoleMenu } from '../../../../my-app/public/assets/role-map/user-role';
import { Menu, TopBarConfig } from '../../../../my-app/public/assets/role-map/menu';
import { KeycloakService } from 'keycloak-angular';

type SfeMeta = {
  quarter_year: string;
  product_category?: string;
  product_platform?: string;
  productPortfolio?: string;
  product_id?: string;
};
@Injectable({
  providedIn: 'root',
})
export class ConfigService {
  private router = inject(Router);
  private globalService = inject(GlobalService);
  private keycloakService = inject(KeycloakService, { optional: true });
  private sfeMetaCache: Partial<SfeMeta> = {};
  apiUrl = environment.apiUrl;
  platformUrl = environment.platformUrl;

  constructor(private http: HttpClient) { }

  getDashboardConfig(configFileName: string): Observable<any> {
    return this.http.get(`/assets/dashboard-config/${configFileName}`);
  }

  getRole(email: string): Observable<string> {
    const user = users.find(
      (u) => u.Email.toLowerCase() === email.toLowerCase()
    );
    return of(user ? user.Role : '');
  }

  getMenu(role: string): Observable<string[]> {
    return of(RoleMenu[role] || []);
  }

  getMenuItems(): Observable<string[]> {
    return Menu;
  }

  getTopBarConfig(): any {
    return TopBarConfig;
  }

  getQuadrant() {
    return this.http.get(`${this.apiUrl}/api/v1/phm/current-year`);
  }

  private drilldownDataSource = new BehaviorSubject<any>(null);
  drilldownData$ = this.drilldownDataSource.asObservable();

  private drilldownTitle = new BehaviorSubject<any>(null);
  drilldownTiltle$ = this.drilldownTitle.asObservable();

  private simulatedDataSource = new BehaviorSubject<any>(null);
  simulatedData$ = this.simulatedDataSource.asObservable();

  private bubbleClickSubject = new BehaviorSubject<string | null>(null);
  bubbleClick$ = this.bubbleClickSubject.asObservable();

  private ganttDropdownOptionsSource = new BehaviorSubject<any>(null);
  ganttDropdownOptions$ = this.ganttDropdownOptionsSource.asObservable();

  setBubbleClicked(widgetId: string) {
    this.bubbleClickSubject.next(widgetId);
  }
  setDrilldownData(data: any) {
    this.drilldownDataSource.next(data);
  }

  setSimulatedData(data: any) {
    this.simulatedDataSource.next(data);
  }
  setDrilldownTitle(data: any) {
    this.drilldownTitle.next(data);
  }

  setGanttDropdownOptions(data: any) {
    this.ganttDropdownOptionsSource.next(data);
  }

  resetDrilldownState(): void {
    this.bubbleClickSubject.next('');
    this.drilldownDataSource.next(null);
    this.drilldownTitle.next(null);
    this.simulatedDataSource.next(null);
  }

  getDrilldown(productId: string) {
    const payload =
    {
      "requestData": {
        "filter": {
          "product_id": [[productId]],
          "entity": "product_header"
        }
      },
      "responseList": [],
      "entity": "phi",
      "workflowId": "ABC-123",
      "endpoints": [{
        "functionName": "DrillDownService",
        "version": "1.0"
      }],
      "communicationType": "rest",
      "version": "1.0"

    };
    return this.http.post(`${this.apiUrl}/spriced/platform`, payload);
  }

  getPdf(key: string) {
    const payload = {
      functionName: 'UserManualService',
      version: '1.0',
      requestData: { key }
    };

    return this.http.post(
      `${this.apiUrl}/api/v1/phm/user-manual`,
      payload,
      { responseType: 'blob' }
    );
  }

  getNotification(userId: string): Observable<any> {
    console.log(`ConfigService: getNotification called for userId: ${userId}`);
    return this.http.get(`${this.platformUrl}/spriced/platform/notification/${userId}`);
  }
 
  getNotificationDetails(payload: { event_id: string, status: string }): Observable<any> {
    return this.http.post(`${this.platformUrl}/spriced/platform/notification/seen`, payload, { responseType: 'text' });
  }
  
  getSimulatedDrilldown(productId: string, data: any) {
    const payload = {
      "requestData": {
        "filter": {
          "attributeNames": ["product_id", "product_details"],
          "filterName": "",
          "orderBy": {},
          where: [
            {
              attributeValue: [productId],
              scope: '',
              attributeName: 'product_id',
              operator: 'IN',
            },
          ],
          "join": {},
          "entity": "product_header"
        },
        "productId": productId,
        "inputNodes": data
      },
      "responseList": [],
      "entity": "phi",
      "workflowId": "ABC-123",
      "endpoints":
        [{
          "functionName": "SimulationDrillDownService",
          "version": "1.0"
        }],
      "communicationType": "rest",
      "version": "1.0"

    }


    return this.http.post(`${this.apiUrl}/spriced/platform`, payload);
  }

  getSimulatedData(productId: string, data: any) {
    const payload = {
      "requestData": {
        "filter": {
          "attributeNames": ["product_id", "product_details"],
          "filterName": "",
          "orderBy": {},
          "where": [
            {
              attributeValue: [productId],
              "scope": "",
              "attributeName": "product_id",
              "operator": "IN"
            }
          ],
          "join": {},
          "entity": "product_header"
        },
        "productId": productId,
        "inputNodes": data
      },
      "responseList": [],
      "entity": "phi",
      "workflowId": "ABC-123",
      "endpoints": [{
        "functionName": "SimulationService",
        "version": "1.0"
      }],
      "communicationType": "rest",
      "version": "1.0"

    }
    return this.http.post(`${this.apiUrl}/spriced/platform`, payload);
  }

  downloadScores() {
    return this.http.get(`${this.apiUrl}/api/v1/phm/export-scores`, {
      responseType: 'blob',
    });
  }

  getScores() {
    return this.http.get('/assets/scores.json');
  }

  getDef() {
    return this.http.get('/assets/definitions.json');
  }

  private buildPayload(requestParamFilters: any): any {
    const currentPath = this.router.url.split('?')[0]; // Remove query params
    const dashboardName = currentPath.startsWith('/')
      ? currentPath.substring(1)
      : currentPath;

    let staticFilters: any[] = [];

    if (dashboardName === 'customer-overview') {
      staticFilters = [
        {
          attributeName: 'customer_name',
          operator: '!=',
          attributeValues: ['NA'],
          logicalOperator: 'AND',
        },
      ];
    } else if (dashboardName !== 'opportunity-overview') {
      staticFilters = [
        {
          attributeName: 'customer_name',
          operator: '!=',
          attributeValues: ['NA'],
          logicalOperator: 'AND',
        },
        {
          attributeName: 'latest_shop_order_flag',
          operator: '=',
          attributeValues: [true],
          logicalOperator: 'AND',
        },
      ];
    }

    const dynamicFilters = Object.entries(requestParamFilters)
      .map(([key, value]) => {
        const rawValues = Array.isArray(value) ? value.flat(Infinity) : [value]; // recursively flatten the array to any depth eg. [[[]]].
        const updatedValues = rawValues.filter(
          (v) =>
            v !== null &&
            v !== undefined &&
            v !== '' &&
            !(Array.isArray(v) && v.length === 0)
        );

        if (updatedValues?.length === 0) return null;

        return {
          attributeName: key,
          operator: updatedValues.length > 1 ? 'IN' : '=',
          attributeValues: updatedValues,
          logicalOperator: 'AND',
        };
      })
      .filter(
        (filter): filter is NonNullable<typeof filter> => filter !== null
      ); //emoves null entries from the array.

    // Adjust logicalOperator handling
    if (dynamicFilters.length > 0) {
      dynamicFilters[dynamicFilters.length - 1].logicalOperator = '';
      if (staticFilters.length > 0) {
        staticFilters[staticFilters.length - 1].logicalOperator = 'AND';
      }
    } else {
      if (staticFilters.length > 0) {
        staticFilters[staticFilters.length - 1].logicalOperator = '';
      }
    }

    return {
      filter: {
        where: [...staticFilters, ...dynamicFilters],
        join: {},
        orderBy: {},
      },
    };
  }

  private buildQueryParams(queryParamFilters: any): HttpParams {
    let params = new HttpParams();
    for (const key in queryParamFilters) {
      const value = queryParamFilters[key];

      if (
        key === 'slider_range' &&
        Array.isArray(value) &&
        value.length === 2
      ) {
        const start = parseInt(value[0], 10);
        const end = parseInt(value[1], 10);

        params = params.set('startRange', start.toString());
        params = params.set('endRange', end.toString());
      } else if (key === 'button') {
        params = params.set(value[0], true);
      } else {
        params = params.set(
          key,
          Array.isArray(value) ? value.join(',') : value
        );
      }
    }

    return params;
  }
  fetchData2(
    url: string,
    functionName: string,
    filters: any,
    data: any,
    method: 'post' | 'get' = 'post',
    options?: { responseType?: 'blob' | 'json'; observe?: 'body' | 'response' }
  ): Observable<any> {
    const useQueryParams = filters?.useQueryParams === true;
    const isDropdownEngineModelRead =
      String(functionName || '') === 'DropdownEngineModelRead';

    // clone filters to avoid modifying the original
    const filterCopy = { ...filters };
    if (isDropdownEngineModelRead && 'new_year' in filterCopy) {
      delete filterCopy.new_year;
    }

    // Remove keys listed in deleteKeys
    if (data?.deleteKeys && Array.isArray(data.deleteKeys)) {
      data.deleteKeys.forEach((key: string) => {
        if (key in filterCopy) {
          delete filterCopy[key];
        }
      });
    }

    // Use the cloned version from here onward
    const requestData: any = {
      ...this.mergeExtraKeys(data.extraKeys),
      ...this.buildFilterPayload(filterCopy, data?.includeMasterFilter || false, data)
    };
    if (data?.costFilter?.length > 0) {
      requestData.costFilter = data.costFilter;
    }
    const payload = {
      requestData,
      "functionName": functionName,
      "communicationType": "rest",
      "version": "1.0"

    };


    const queryParams = useQueryParams
      ? this.buildQueryParams(filterCopy?.queryParams || {})
      : new HttpParams();

    const httpOptions: any = { params: queryParams, ...options };

    return method === 'post'
      ? this.http.post(this.apiUrl + url, payload, httpOptions)
      : this.http.get(this.apiUrl + url, httpOptions);
  }
  fetchData(
    url: string,
    functionName: string,
    filters: any,
    data: any,
    method: 'post' | 'get' = 'post',
    options?: { responseType?: 'blob' | 'json'; observe?: 'body' | 'response' },
    email?: string
  ): Observable<any> {
    if (data?.directApi === true) {
      const directPayload = data?.requestPayload ?? filters ?? {};
      const queryParams = filters?.useQueryParams === true
        ? this.buildQueryParams(filters?.queryParams || {})
        : new HttpParams();
      const finalUrl = /^https?:\/\//.test(url) ? url : this.apiUrl + url;
      const httpOptions: any = { params: queryParams, ...options };

      return method === 'post'
        ? this.http.post(finalUrl, directPayload, httpOptions)
        : this.http.get(finalUrl, httpOptions);
    }

    if (String(functionName || '') === 'UserEmailDropdown') {
      const payload = {
        requestData: {
          filter: {},
        },
        responseList: [],
        entity: 'phi',
        workflowId: 'ABC-123',
        endpoints: [
          {
            functionName: 'UserEmailDropdown',
            version: '1.0',
          },
        ],
        communicationType: 'rest',
        version: '1.0',
      };

      const httpOptions: any = { params: new HttpParams(), ...(options || {}) };

      return method === 'post'
        ? this.http.post(this.apiUrl + url, payload, httpOptions)
        : this.http.get(this.apiUrl + url, httpOptions);
    }

    const useQueryParams = filters?.useQueryParams === true;
    const isDropdownEngineModelRead =
      String(functionName || '') === 'DropdownEngineModelRead';

    // clone filters to avoid modifying the original
    const filterCopy = { ...filters };
    if (isDropdownEngineModelRead && 'new_year' in filterCopy) {
      delete filterCopy.new_year;
    }

    // Remove keys listed in deleteKeys
    if (data?.deleteKeys && Array.isArray(data.deleteKeys)) {
      data.deleteKeys.forEach((key: string) => {
        if (key in filterCopy) {
          delete filterCopy[key];
        }
      });
    }

    // Use the cloned version from here onward
    const customRequestFilter = data?.requestFilter;
    const explicitRequestDataFilter = this.buildExplicitRequestDataFilter(data);
    const selectedRequestDataFilter = this.buildSelectedRequestDataFilter(
      filterCopy,
      data
    );
    const resolvedFilterPayload =
      explicitRequestDataFilter
        ? {
          filter: {
            ...explicitRequestDataFilter,
            ...selectedRequestDataFilter,
          },
        }
        : customRequestFilter && typeof customRequestFilter === 'object'
        ? {
          filter: this.mergeCustomRequestFilter(
            customRequestFilter,
            data?.customFilters || []
          ),
        }
        : this.buildFilterPayload(filterCopy, data?.includeMasterFilter || false, data);

    const requestData: any = {
      ...this.mergeExtraKeys(data.extraKeys),
      ...resolvedFilterPayload
    };
    this.attachRequestDataKeys(requestData, filterCopy, data);
    if (data?.costFilter?.length > 0) {
      requestData.costFilter = data.costFilter;
    }
    const payload: any = {
      requestData,
      "responseList": [],
      "entity": data?.entity || "phi",
      "workflowId": "ABC-123",
      "endpoints": [{
        "functionName": functionName || '',
        "version": "1.0"
      }],
      "communicationType": "rest",
      "version": "1.0"

    };

    if (data?.omitBatchId !== true) {
      payload["batchId"] = "test";
    }
    if (data?.includeUserId === true || this.shouldAttachIdeationUserId(functionName)) {
      const keycloak = this.keycloakService?.getKeycloakInstance();
      payload["userId"] =
        email ||
        keycloak?.idTokenParsed?.['email'] ||
        keycloak?.tokenParsed?.['email'] ||
        null;
    }


    const queryParams = useQueryParams
      ? this.buildQueryParams(filterCopy?.queryParams || {})
      : new HttpParams();

    const httpOptions: any = { params: queryParams, ...options };

    if (data?.debugKey) {
      console.log(`[${data.debugKey}] ConfigService.fetchData:payload`, {
        url: this.apiUrl + url,
        method,
        functionName,
        filterCopy,
        customRequestFilter,
        explicitRequestDataFilter,
        requestData,
        payload,
      });
    }

    return method === 'post'
      ? this.http.post(this.apiUrl + url, payload, httpOptions)
      : this.http.get(this.apiUrl + url, httpOptions);
  }

  private attachRequestDataKeys(requestData: any, filters: any, data: any): void {
    const keys = Array.isArray(data?.requestDataKeys) ? data.requestDataKeys : [];
    keys.forEach((rawKey: any) => {
      const filterKey = String(rawKey || '').trim();
      if (!filterKey || filters?.[filterKey] == null) {
        return;
      }

      const value = this.unwrapRequestDataValue(filters[filterKey]);
      if (value === '' || value === 'All' || value === null || value === undefined) {
        return;
      }

      requestData[filterKey] = value;
    });
  }

  private buildExplicitRequestDataFilter(data: any): Record<string, any> | null {
    const entries = Array.isArray(data?.requestDataFilterKeys)
      ? data.requestDataFilterKeys
      : [];

    if (!entries.length) {
      return null;
    }

    const filterPayload: Record<string, any> = {};
    entries.forEach((entry: any) => {
      if (typeof entry === 'string') {
        const key = entry.trim();
        if (key) {
          filterPayload[key] = [];
        }
        return;
      }

      const key = String(entry?.key || entry?.attributeName || '').trim();
      if (!key) {
        return;
      }

      filterPayload[key] = entry.value ?? entry.attributeValue ?? [];
    });

    return filterPayload;
  }

  private buildSelectedRequestDataFilter(
    filters: Record<string, any>,
    data: any
  ): Record<string, any> {
    const selectedKeys = Array.isArray(data?.requestDataSelectedFilterKeys)
      ? data.requestDataSelectedFilterKeys
      : [];
    const scalarKeys = new Set(
      Array.isArray(data?.requestDataScalarFilterKeys)
        ? data.requestDataScalarFilterKeys
        : []
    );
    const selectedFilter: Record<string, any> = {};

    selectedKeys.forEach((rawKey: any) => {
      const key = String(rawKey || '').trim();
      const rawValue = filters?.[key];
      const values = (Array.isArray(rawValue) ? rawValue.flat(Infinity) : [rawValue])
        .filter(
          (value: any) =>
            value !== null &&
            value !== undefined &&
            value !== '' &&
            value !== 'All'
        );

      if (!key || values.length === 0) {
        return;
      }

      selectedFilter[key] = scalarKeys.has(key) ? values[0] : [values];
    });

    return selectedFilter;
  }

  private unwrapRequestDataValue(value: any): any {
    if (Array.isArray(value)) {
      if (value.length === 1) {
        return this.unwrapRequestDataValue(value[0]);
      }
      return value;
    }

    if (value && typeof value === 'object') {
      return value.value ?? value.id ?? value.name ?? value.viewValue ?? value;
    }

    return value;
  }

  downloadExcel(
    url: string,
    functionName: string,
    filters: any,
    data: any,
    email: string,
    exportColumns: Array<{ prop: string; name: string }> = [],
    options?: { responseType?: 'blob' | 'json'; observe?: 'body' | 'response' }
  ): Observable<any> {
    const modifiedFilters: { [key: string]: (string[])[] } = {};
    for (const key in filters) {
      if (Object.prototype.hasOwnProperty.call(filters, key)) {
        modifiedFilters[key] = [filters[key]];
      }
    }

    const configuredPayload = data?.downloadExcelPayload;
    const payload = configuredPayload
      ? {
          entity: configuredPayload.entity,
          userId: email,
          filename: configuredPayload.filename,
          isDeamExport: configuredPayload.isDeamExport,
          filter: {
            filterName: functionName,
            filterValues: { ...(configuredPayload.filterValues || {}) },
          },
        }
      : {
          userId: email,
          entity: 'ecosystem',
          filename: String(filters.project_category ?? ''),
          displayedColumnsOrder: ['sno', ...exportColumns.map((column) => column.prop)],
          filter: {
            filterName: functionName,
            filterValues: modifiedFilters,
            displayedColumnsOrder: ['sno', ...exportColumns.map((column) => column.prop)],
            columnOrder: exportColumns.map((column) => column.prop),
            columns: exportColumns,
          },
          columns: exportColumns,
        };


    const httpOptions: any = { ...options };
    const finalUrl = /^https?:\/\//.test(url) ? url : this.apiUrl + url;

    return this.http.post(finalUrl, payload, httpOptions);
  }

  //   buildFilterPayload2(
  //   filters: { [key: string]: string[] | string },
  //   includeMasterFilter = false,
  //   data: any
  // ) {
  //   const output: any = {};

  //   // handle master filter
  //   if (includeMasterFilter) {
  //     filters['masterfilter'] = ['Include'];
  //   } else {
  //      filters['masterfilter'] = ['Exclude'];
  //   }

  //   // convert normal filters
  //   Object.entries(filters).forEach(([key, values]) => {

  //     if (values == null) return;

  //     // handle string filters (do NOT wrap in [])
  //     if (typeof values === 'string') {
  //       if (values === 'All') return;
  //       output[key] = values;   // <-- no array
  //       return;
  //     }

  //     // handle array filters
  //     if (!Array.isArray(values) || values.length === 0 || values.includes('All')) {
  //       return;
  //     }

  //     // special cleaned_vertical handling
  //     if (key === 'cleaned_vertical') {
  //       values = values.map((v) =>
  //         v.toLowerCase() === 'o&g' ? 'OIL & GAS' : v.toUpperCase()
  //       );
  //     }

  //     output[key] = [values];  // normal behavior for arrays
  //   });

  //   // custom filters (keep existing behavior)
  //   if (data?.customFilters?.length) {
  //     data.customFilters.forEach((cf: any) => {
  //       if (cf.attributeName && cf.attributeValue) {
  //         output[cf.attributeName] = [cf.attributeValue];
  //       }
  //     });
  //   }

  //   // default filter
  //   if (Object.keys(output).length === 0) {
  //     output["is_active"] = [["true"]];
  //   }

  //   return {
  //     filter: output,
  //   };
  // }

  buildFilterPayload(
    filters: { [key: string]: string[] | string },
    includeMasterFilter = false,
    data: any
  ) {
    const output: any = {};

    // handle master filter
    if (includeMasterFilter) {
      filters['masterfilter'] = ['Include'];
    } else {
      delete filters['masterfilter'];
    }

    // convert normal filters
    Object.entries(filters).forEach(([key, values]) => {

      if (values == null) return;

      // handle string filters (do NOT wrap in [])
      if (typeof values === 'string') {
        if (values === 'All') return;
        output[key] = values;   // <-- no array
        return;
      }

      // handle array filters
      if (!Array.isArray(values) || values.length === 0 || values.includes('All')) {
        return;
      }

      // special cleaned_vertical handling
      if (key === 'cleaned_vertical') {
        values = values.map((v) =>
          v.toLowerCase() === 'o&g' ? 'OIL & GAS' : v.toUpperCase()
        );
      }

      output[key] = [values];  // normal behavior for arrays
    });

    // custom filters (keep existing behavior)
    if (data?.customFilters?.length) {
      data.customFilters.forEach((cf: any) => {
        if (cf.attributeName && cf.attributeValue) {
          output[cf.attributeName] = [cf.attributeValue];
        }
      });
    }

    // default filter
    if (Object.keys(output).length === 0) {
      output["is_active"] = [["true"]];
    }

    return {
      filter: output,
    };
  }


  getApi(
    url: string,
    functionName: string,
    filters: any,
    data: any,
    method: 'post' | 'get' = 'post',
    options?: { responseType?: 'blob' | 'json'; observe?: 'body' | 'response' }
  ): Observable<any> {
    const useQueryParams = filters?.useQueryParams === true;
    const isDropdownEngineModelRead =
      String(functionName || '') === 'DropdownEngineModelRead';

    // clone filters to avoid modifying the original
    const filterCopy = { ...filters };
    if (isDropdownEngineModelRead && 'new_year' in filterCopy) {
      delete filterCopy.new_year;
    }

    // Remove keys listed in deleteKeys
    if (data?.deleteKeys && Array.isArray(data.deleteKeys)) {
      data.deleteKeys.forEach((key: string) => {
        if (key in filterCopy) {
          delete filterCopy[key];
        }
      });
    }

    // Use the cloned version from here onward
    const requestData: any = {
      ...this.mergeExtraKeys(data.extraKeys),
      filter: this.createPayload(
        filterCopy,
        data?.attributeNames || [],
        data?.includeMasterFilter || false,
        data
      ),
    };

    if (data?.costFilter?.length > 0) {
      requestData.costFilter = data.costFilter;
    }

    const payload = {
      functionName: functionName || '',
      version: '1.0',
      requestData,
    };

    const queryParams = useQueryParams
      ? this.buildQueryParams(filterCopy?.queryParams || {})
      : new HttpParams();

    const httpOptions: any = { params: queryParams, ...options };

    const shouldUseLegacyBubblePayload =
      data?.isBubbleChart === true &&
      data?.useLegacyPayloadStructure === true;

    if (shouldUseLegacyBubblePayload) {
      const legacyRequestData: any = {
        ...this.mergeExtraKeys(data.extraKeys),
        ...this.buildFilterPayload(
          filterCopy,
          data?.includeMasterFilter || false,
          data
        ),
      };

      if (data?.costFilter?.length > 0) {
        legacyRequestData.costFilter = data.costFilter;
      }

      const legacyPayload = {
        requestData: legacyRequestData,
        responseList: [],
        entity: 'phi',
        batchId: 'test',
        workflowId: 'ABC-123',
        endpoints: [
          {
            functionName: functionName || '',
            version: '1.0',
          },
        ],
        communicationType: 'rest',
        version: '1.0',
      };

      return method === 'post'
        ? this.http.post(this.apiUrl + url, legacyPayload, httpOptions)
        : this.http.get(this.apiUrl + url, httpOptions);
    }

    return method === 'post'
      ? this.http.post(this.apiUrl + url, payload, httpOptions)
      : this.http.get(this.apiUrl + url, httpOptions);
  }
  customApi(
    url: string,
    payload: any,
    data: any,
    method: 'post' | 'get' = 'post'
  ): Observable<any> {
    if (data?.directApi === true || data?.direct === true) {
      const finalUrl = /^https?:\/\//.test(url) ? url : this.apiUrl + url;
      return method === 'post'
        ? this.http.post(finalUrl, payload)
        : this.http.get(finalUrl);
    }

    return method === 'post'
      ? this.http.post(this.apiUrl + url, payload)
      : this.http.get(this.apiUrl + url);
  }
  getSimulateConfigData() {
    return this.http.get(this.apiUrl + '/api/v1/phm/config');
  }
  saveSimulateConfigData(data: any) {
    return this.http.post(this.apiUrl + '/api/v1/phm/config', data);
  }

  // Helper function
  private mergeExtraKeys(extraKeys: any[]): any {
    if (!Array.isArray(extraKeys)) return {};
    return extraKeys.reduce((acc, obj) => ({ ...acc, ...obj }), {});
  }

  private mergeCustomRequestFilter(
    requestFilter: Record<string, any>,
    customFilters: any[]
  ): Record<string, any> {
    const mergedFilter: Record<string, any> = { ...(requestFilter || {}) };

    if (Array.isArray(customFilters)) {
      customFilters.forEach((cf: any) => {
        if (cf?.attributeName && cf?.attributeValue !== undefined) {
          mergedFilter[cf.attributeName] = [cf.attributeValue];
        }
      });
    }

    return mergedFilter;
  }




  createPayload(
    filters: { [key: string]: string[] },
    attributeNames: string[] = [],
    includeMasterFilter = false,
    data: any
  ) {
    const omitLastLogicalOperator = data?.omitLastLogicalOperator === true;
    const omitEmptyAttributeNames = data?.omitEmptyAttributeNames === true;

    const withLogicalOperator = (
      condition: any,
      isLast: boolean
    ): any => {
      if (omitLastLogicalOperator && isLast) {
        const { logicalOperator, ...rest } = condition;
        return rest;
      }

      return {
        ...condition,
        logicalOperator: isLast ? '' : 'AND',
      };
    };

    if (includeMasterFilter) {
      filters['masterfilter'] = ['Include'];
    } else {
      delete filters['masterfilter'];
    }

    const keys = Object.keys(filters);

    let where = keys
      .map((key) => {
        let values = filters[key];
        if (!values || values.length === 0 || values.includes('All')) {
          return null;
        }

        if (key === 'cleaned_vertical') {
          values = values.map((v) =>
            v.toLowerCase() === 'o&g' ? 'OIL & GAS' : v.toUpperCase()
          );
        }

        return {
          attributeName: key,
          operator: 'IN',
          attributeValue: values,
        };
      })
      .filter(Boolean)
      .map((condition, index, arr) =>
        withLogicalOperator(condition!, index === arr.length - 1)
      );

    // merge data.customFilters if present
    if (data?.customFilters?.length) {
      if (where.length > 0) {
        where[where.length - 1].logicalOperator = 'AND';
      }

      const custom = data.customFilters.map((cf: any, index: number) =>
        withLogicalOperator(cf, index === data.customFilters.length - 1)
      );
      where = [...where, ...custom];
    }

    const payload: any = {
      where,
      join: {},
      orderBy: data.orderBy || {},
    };

    if (!(omitEmptyAttributeNames && (!attributeNames || attributeNames.length === 0))) {
      payload.attributeNames = attributeNames;
    }

    return payload;
  }

  saveFutureExpectations(payload: any, url: string) {
    const finalPayload = {
      requestData: payload,
      "responseList": [],
      "entity": "phi",
      "workflowId": "ABC-123",
      "endpoints": [
        {
          "functionName": "ProductMetricsDrilldownInsertAndUpdateService",
          "version": "1.0"
        }
      ],
      "communicationType": "rest",
      "version": "1.0"
    }
    return this.http.post(this.apiUrl + url, finalPayload);
  }

  getConfigData(url: string) {
    return this.http.get(url);
  }

  saveData(payload: any, source: string, data: any, email: string) {
    if (data?.savePayloadConfig?.mode === 'flatRequestData') {
      return this.http.post(
        this.apiUrl + data.saveUrl,
        this.createFlatRequestDataSavePayload(payload, data, email)
      );
    }

    const createdBy = this.resolveCreatedBy(payload, email);
    const sanitizedPayload =
      data?.saveFunction === 'PheInsertUpdateService'
        ? this.removeEmptyFields(payload)
        : payload;
    const planningYearKey = String(
      data?.planningYearSelector?.requestKey || 'ecosystem_active_year'
    ).trim();
    const planningYearValue =
      this.resolvePlanningYearFromPayload(payload, planningYearKey) ??
      this.resolvePlanningYearValue(planningYearKey);
    const payloadWithPlanningYear =
      planningYearValue == null
        ? sanitizedPayload
        : this.attachPlanningYearToSavedPayload(
            sanitizedPayload,
            planningYearKey,
            planningYearValue
          );
    const configuredUserRole = this.resolveConfiguredUserRole(data, email);
    const requestDataContext = this.resolveSaveRequestDataContext(data, payloadWithPlanningYear);
    const finalSavedPayload = this.applySaveRowContext(
      payloadWithPlanningYear,
      data,
      requestDataContext
    );

    const finalPayload = {
      requestData: {
        ...(data.saveKey !== 'user_revenue' ? { source } : {}),
        created_by: createdBy,
        ...(planningYearValue == null ? {} : { [planningYearKey]: planningYearValue }),
        ...(configuredUserRole
          ? { [configuredUserRole.key]: configuredUserRole.value }
          : {}),
        ...requestDataContext,
        [data.saveKey]: finalSavedPayload,
      },
      "responseList": [],
      "entity": "phi",
      "workflowId": "ABC-123",
      "endpoints": [
        {
          "functionName": data.saveFunction,
          "version": "1.0"
        }
      ],
      "userId": data?.saveFunction === 'PheInsertUpdateService' ? createdBy : null,
      "communicationType": "rest",
      "version": "1.0"
    }
    return this.http.post(this.apiUrl + data.saveUrl, finalPayload);
  }

  private applySaveRowContext(payload: any, data: any, context: Record<string, any>): any {
    const keys = Array.isArray(data?.savePayloadConfig?.applyRequestDataKeysToRows)
      ? data.savePayloadConfig.applyRequestDataKeysToRows
      : [];
    if (!keys.length) {
      return payload;
    }

    const applyContext = (row: any): any => {
      if (!row || typeof row !== 'object' || Array.isArray(row)) {
        return row;
      }

      const nextRow = { ...row };
      keys.forEach((rawKey: any) => {
        const key = String(rawKey || '').trim();
        if (key && context[key] !== undefined) {
          nextRow[key] = context[key];
        }
      });
      return nextRow;
    };

    return Array.isArray(payload) ? payload.map(applyContext) : applyContext(payload);
  }

  private createFlatRequestDataSavePayload(payload: any[], data: any, email: string): any {
    const config = data?.savePayloadConfig || {};
    const changedRows = Array.isArray(payload) ? payload : [payload];
    const mappedRows = changedRows.map((row) =>
      this.mapSaveRow(row, data?.headers || [], config, email)
    );
    const requestData =
      config.rowMode === 'array' ? mappedRows : mappedRows[0] || {};

    return {
      entity: config.entity || data?.entity || '',
      requestData,
      responseList: [],
      workflowId: config.workflowId || data?.workflowId || '',
      endpoints: [
        {
          functionName: data.saveFunction,
          version: config.endpointVersion || '1.0',
        },
      ],
      version: config.version || '1.0',
    };
  }

  private mapSaveRow(row: any, headers: any[], config: any, email: string): any {
    const result: any = {};
    const includeProps = Array.isArray(config.includeProps)
      ? config.includeProps
      : headers.map((header: any) => header?.requestProp || header?.prop);

    headers.forEach((header: any) => {
      const sourceProp = header?.prop;
      const requestProp = header?.requestProp || sourceProp;
      if (!sourceProp || !requestProp || !includeProps.includes(requestProp)) {
        return;
      }

      let value = row[sourceProp] ?? row[requestProp];
      if (header?.saveValueMap && value in header.saveValueMap) {
        value = header.saveValueMap[value];
      }

      if (header?.saveFormat === 'isoWithOffset') {
        value = this.formatIsoWithOffset(value, config.timezoneOffset || '+05:30');
      }

      result[requestProp] = value;
    });

    this.applySaveMetadata(result, row, config, email);
    return result;
  }

  private applySaveMetadata(result: any, row: any, config: any, email: string): void {
    const rowStatus = String(row?.__row_status || '').toLowerCase();
    const auditConfig = config.auditFields || {};
    const userValue = this.resolveSaveUserValue(auditConfig, email);

    if (rowStatus === 'new' && auditConfig.createdBy) {
      result[auditConfig.createdBy] = userValue;
    } else if (auditConfig.updatedBy) {
      result[auditConfig.updatedBy] = userValue;
    }

    if ((rowStatus === 'deleted' || row?.is_deleted === true) && config.deleteFlag?.key) {
      result[config.deleteFlag.key] =
        config.deleteFlag.value !== undefined ? config.deleteFlag.value : true;
    }
  }

  private resolveSaveUserValue(auditConfig: any, email: string): string {
    if (auditConfig.staticValue !== undefined) {
      return auditConfig.staticValue;
    }

    if (auditConfig.value === 'email') {
      return email || '';
    }

    if (auditConfig.value === 'emailPrefix') {
      return String(email || '').split('@')[0];
    }

    return auditConfig.value || email || '';
  }

  private formatIsoWithOffset(value: any, offset: string): any {
    if (!value) {
      return value;
    }

    if (typeof value === 'string') {
      const dateOnly = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (dateOnly) {
        const year = Number(dateOnly[1]);
        const month = Number(dateOnly[2]);
        const day = Number(dateOnly[3]);
        if ([year, month, day].every(Number.isFinite)) {
          const pad = (part: number) => String(part).padStart(2, '0');
          return `${year}-${pad(month)}-${pad(day)}T00:00:00${offset}`;
        }
      }
    }

    const parsed = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return value;
    }

    const date = new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
    const pad = (part: number) => String(part).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T00:00:00${offset}`;
  }

  private resolveConfiguredUserRole(
    data: any,
    email: string
  ): { key: string; value: string } | null {
    const userRoleConfig = data?.savePayloadConfig?.userRole;
    const key = String(userRoleConfig?.key || '').trim();
    if (!key) {
      return null;
    }

    const defaultValue = String(userRoleConfig?.defaultValue || 'USER');
    const role = users.find(
      (user) => user.Email.toLowerCase() === String(email || '').toLowerCase()
    )?.Role;
    const adminRoles = Array.isArray(userRoleConfig?.adminRoles)
      ? userRoleConfig.adminRoles
      : [];
    const value = role && adminRoles.includes(role)
      ? String(userRoleConfig?.adminValue || 'ADMIN')
      : defaultValue;

    return { key, value };
  }

  private resolveSaveRequestDataContext(data: any, payload: any): Record<string, any> {
    const keys = Array.isArray(data?.savePayloadConfig?.requestDataKeys)
      ? data.savePayloadConfig.requestDataKeys
      : [];
    if (!keys.length) {
      return {};
    }

    const filters = Array.isArray(data?.additionalFilters)
      ? data.additionalFilters
      : [];
    const context: Record<string, any> = {};

    keys.forEach((rawKey: any) => {
      const key = String(rawKey || '').trim();
      if (!key) {
        return;
      }

      if (data?.savePayloadConfig?.requestDataKeysFromRows?.includes(key)) {
        const rows = Array.isArray(payload) ? payload : [payload];
        const row = rows.find((item: any) =>
          item?.[key] !== undefined && item?.[key] !== null && item?.[key] !== ''
        );
        if (row) {
          context[key] = this.unwrapRequestDataValue(row[key]);
          return;
        }
      }

      const filter = filters.find(
        (item: any) =>
          item &&
          typeof item === 'object' &&
          Object.prototype.hasOwnProperty.call(item, key)
      );
      if (!filter) {
        return;
      }

      context[key] = this.unwrapRequestDataValue(filter[key]);
    });

    return context;
  }

  private resolveCreatedBy(payload: any, fallbackEmail: string): string {
    if (Array.isArray(payload)) {
      const firstWithCreatedBy = payload.find(
        (row: any) =>
          typeof row?.created_by === 'string' && row.created_by.trim() !== ''
      );
      if (firstWithCreatedBy?.created_by) {
        return firstWithCreatedBy.created_by;
      }
    } else if (
      payload &&
      typeof payload?.created_by === 'string' &&
      payload.created_by.trim() !== ''
    ) {
      return payload.created_by;
    }

    return fallbackEmail;
  }

  private shouldAttachIdeationUserId(functionName: string): boolean {
    const name = String(functionName || '').toLowerCase();
    return (
      name === 'ideationreadservice' ||
      name === 'simulationreadservice' ||
      name === 'aopreadservice'
    );
  }

  private resolvePlanningYearValue(planningYearKey: string): number | null {
    const currentFilters = this.globalService.getCurrentFilters?.();
    const rawValue = currentFilters?.filter?.[planningYearKey];
    const candidate = Array.isArray(rawValue) ? rawValue[0] : rawValue;
    const parsed = Number(Array.isArray(candidate) ? candidate[0] : candidate);

    return Number.isFinite(parsed) ? parsed : null;
  }

  private attachPlanningYearToSavedPayload(
    payload: any,
    planningYearKey: string,
    planningYearValue: number
  ): any {
    if (Array.isArray(payload)) {
      return payload.map((item) =>
        item && typeof item === 'object'
          ? { ...item, [planningYearKey]: planningYearValue }
          : item
      );
    }

    if (payload && typeof payload === 'object') {
      return {
        ...payload,
        [planningYearKey]: planningYearValue,
      };
    }

    return payload;
  }

  private removeEmptyFields(value: any): any {
    if (Array.isArray(value)) {
      return value
        .map((item) => this.removeEmptyFields(item))
        .filter((item) => !this.shouldOmitEmptyValue(item));
    }

    if (value && typeof value === 'object') {
      const sanitizedObject: Record<string, any> = {};

      Object.entries(value).forEach(([key, nestedValue]) => {
        const sanitizedValue = this.removeEmptyFields(nestedValue);
        if (this.shouldOmitEmptyValue(sanitizedValue)) {
          return;
        }

        sanitizedObject[key] = sanitizedValue;
      });

      return sanitizedObject;
    }

    return value;
  }

  private shouldOmitEmptyValue(value: any): boolean {
    if (value === null || value === undefined) {
      return true;
    }

    if (typeof value === 'string') {
      return value.trim() === '';
    }

    if (Array.isArray(value)) {
      return value.length === 0;
    }

    if (typeof value === 'object') {
      return Object.keys(value).length === 0;
    }

    return false;
  }

  upload(file: File, url: string, projectCategory: string, email: string): Observable<any> {
    return this.http.post(
      this.apiUrl + url + '/' + projectCategory + '/' + email,
      file, // send the File (Blob) directly as the body
      {
        headers: new HttpHeaders({
          'Content-Type': 'application/octet-stream',
        }),
        withCredentials: true, // keep if you rely on cookies; otherwise remove
        responseType: 'json',
      }
    );
  }

  getConfig(
    url: string,
    filters?: any,
    method: 'get' | 'post' = 'post',
    isTable = false
  ): Observable<any> {
    const currentPath = this.router.url.split('?')[0];
    const dashboardName = currentPath.startsWith('/')
      ? currentPath.substring(1)
      : currentPath;

    // --- Special handling for simulate2 ---
    if (dashboardName.includes('simulate2')) {
      const queryParamFilters: any = {};
      const payload: any = {};

      if (filters) {
        // Split filters vs. extra payload
        for (const key in filters) {
          if (['cleaned_vertical', 'final_em', 'new_year'].includes(key)) {
            // filters → query params
            if (key === 'cleaned_vertical')
              queryParamFilters['segment'] = filters[key];
            else if (key === 'final_em')
              queryParamFilters['platform'] = filters[key];
            else if (key === 'new_year')
              queryParamFilters['year'] = filters[key];
          } else {
            // everything else → payload
            payload[key] = filters[key];
          }
        }
      }

      // Remove "All" values from query params
      for (const key in queryParamFilters) {
        if (
          Array.isArray(queryParamFilters[key]) &&
          queryParamFilters[key].includes('All')
        ) {
          delete queryParamFilters[key];
        }
      }

      const httpParams = this.buildQueryParams(queryParamFilters);

      // GET → only query params
      if (method === 'get') {
        return this.http.get<any>(url, { params: httpParams });
      }

      // POST → filters in query params, rest in payload
      return this.http.post<any>(url, payload, { params: httpParams });
    }

    // --- Default handling for other dashboards ---
    const useQueryParams = filters?.useQueryParams === true;

    if (filters && 'useQueryParams' in filters) {
      delete filters.useQueryParams;
    }

    const payload = this.buildPayload(filters?.requestParams || filters || {});
    const queryParams = useQueryParams
      ? this.buildQueryParams(filters?.queryParams || {})
      : new HttpParams();

    if (!!isTable && !dashboardName.includes('simulate2')) {
      payload.filter.where = payload.filter.where.filter(
        (elm: any) => elm.attributeName === 'customer_spec'
      );
    }

    return method === 'post'
      ? this.http.post<any>(url, payload, { params: queryParams })
      : this.http.get<any>(url, { params: queryParams });
  }

  getDropdownOptions(url: string, payload: any): Observable<any> {
    return this.http.post(url, payload);
  }

  private resolvePlanningYearFromPayload(
    payload: any,
    planningYearKey: string
  ): number | null {
    const rows = Array.isArray(payload) ? payload : [payload];
    for (const row of rows) {
      const parsed = Number(row?.[planningYearKey]);
      if (Number.isFinite(parsed)) return parsed;
    }
    return null;
  }

  getEntityStoreMetadata(url: string, payload: any): Observable<any> {
    return this.http.post(`${this.apiUrl}${url}`, payload);
  }

  getLandingPageEngineModels(): Observable<any> {
    const payload = {
      requestData: {
        filter: {
          is_active: [['true']],
          cleaned_vertical: [[]],
        },
      },
      responseList: [],
      entity: 'phi',
      batchId: 'test',
      workflowId: 'ABC-123',
      endpoints: [
        {
          functionName: 'DropdownEngineModelRead',
          version: '1.0',
        },
      ],
      communicationType: 'rest',
      version: '1.0',
    };

    return this.http.post(`${this.apiUrl}/spriced/platform`, payload);
  }

  getData(url: string) {
    return this.http.get(url);
  }
}
